import type { ComboboxItem } from '@mantine/core';
import type { AddressOption } from '@/types/buy';
import { addressOptionKey, buildAddressOptions } from '@/lib/addressOptions';
import { formatOrgName } from '@/lib/format';

/**
 * Выбранный базис. В best-price значимы только `address` + `addressTitle` (для отображения/ключа);
 * `basisId` нужен формам создания (предложение/прайс-лист) — пробрасывается, если известен
 * (есть у «моих базисов» из дерева; у результатов справочного поиска `basisId` нет).
 */
export interface BasisValue {
  readonly address: string;
  readonly addressTitle: string;
  readonly basisId?: number;
}

/** Свой базис (точка моих прайсов/предложений) на входе пикера. */
export interface OwnBasis {
  readonly address: string;
  readonly addressTitle: string;
  readonly basisId?: number;
}

/** Опция Select в пикере базиса: + `isOwn` (мой базис) для индикатора и группировки. */
export interface BasisPickerItem extends ComboboxItem {
  readonly title?: string;
  readonly address?: string;
  readonly isOwn: boolean;
}

export interface BasisOptionGroup {
  readonly group: string;
  readonly items: BasisPickerItem[];
}

export interface AssembledBasisData {
  /** Группы для Mantine `Select.data` (пустые группы не включаются). */
  readonly data: BasisOptionGroup[];
  /** value опции → выбранный базис (для onChange без обратного парсинга label). */
  readonly lookup: Map<string, BasisValue>;
}

/** Строка выбранного значения: «Название — адрес», либо просто адрес, если названия нет. */
export function basisLabel(value: { readonly address: string; readonly addressTitle?: string }): string {
  return value.addressTitle ? `${formatOrgName(value.addressTitle)} — ${value.address}` : value.address;
}

/** Регистронезависимое вхождение всех слов запроса в строку (по словам, как ищет бэкенд). */
function matchesQuery(haystack: string, query: string): boolean {
  const words = query.toLowerCase().trim().split(/\s+/).filter(Boolean);
  const lower = haystack.toLowerCase();
  return words.every((word) => lower.includes(word));
}

/**
 * Собирает сгруппированные опции базиса: «Мои базисы» (свои, локальный фильтр по запросу) + «Справочник»
 * (результаты /order/address/search). Дедуп по ключу address+title (свои выигрывают — результат
 * справочника, совпавший со своим базисом, в группу справочника не попадает). Пустые группы НЕ
 * включаются: нет своих базисов (или ни один не совпал с запросом) → группы «Мои базисы» не будет вовсе.
 * Mantine требует уникальные value по всему data — поэтому ведём общий set ключей.
 */
export function assembleBasisData(params: {
  readonly ownBases: readonly OwnBasis[];
  readonly directory: readonly AddressOption[];
  /** Активный поисковый запрос; '' → показывать все свои базисы без фильтра. */
  readonly query: string;
  readonly ownGroupLabel: string;
  readonly directoryGroupLabel: string;
}): AssembledBasisData {
  const { ownBases, directory, query, ownGroupLabel, directoryGroupLabel } = params;
  const seen = new Set<string>();
  const lookup = new Map<string, BasisValue>();
  const trimmedQuery = query.trim();

  const ownItems: BasisPickerItem[] = [];
  for (const base of ownBases) {
    const value = addressOptionKey({ address: base.address, title: base.addressTitle });
    if (seen.has(value)) {
      continue; // схлопываем дубликаты своих базисов
    }
    if (trimmedQuery && !matchesQuery(`${base.addressTitle} ${base.address}`, trimmedQuery)) {
      continue; // локальный фильтр своих по запросу
    }
    seen.add(value);
    lookup.set(value, { address: base.address, addressTitle: base.addressTitle, basisId: base.basisId });
    ownItems.push({ value, label: basisLabel(base), title: base.addressTitle, address: base.address, isOwn: true });
  }

  const directoryItems: BasisPickerItem[] = [];
  for (const option of buildAddressOptions(directory)) {
    if (seen.has(option.value)) {
      continue; // уже показан как «мой базис» — в справочник не дублируем
    }
    seen.add(option.value);
    const { address, title, basisId } = option.option;
    lookup.set(option.value, { address, addressTitle: title ?? '', basisId });
    directoryItems.push({ value: option.value, label: option.label, title, address, isOwn: false });
  }

  const data: BasisOptionGroup[] = [];
  if (ownItems.length > 0) {
    data.push({ group: ownGroupLabel, items: ownItems });
  }
  if (directoryItems.length > 0) {
    data.push({ group: directoryGroupLabel, items: directoryItems });
  }
  return { data, lookup };
}
